

<template>
  <div ref="gantt-import-mapper-header-container" class="gantt-import-mapper-header-container">
    <div class="min-height-17">
      <span v-if="canChangeDirection()">
        <b-popover
          :target="`HEADER_DIRECTION_${params.column.colId}`"
          placement="top"
          triggers="hover"
          :content="direction() === 'h' ? $t('task.row') : $t('task.column')">
        </b-popover>
        <button :id="`HEADER_DIRECTION_${params.column.colId}`" class="btn-action" @click="toggleDirection"><font-awesome-icon :icon="direction() === 'h' ? ['far','arrows-left-right'] : ['far','arrows-up-down']"/></button> 
      </span>
      <span v-else>
        &nbsp;
      </span>
      {{ displayValue }}
    </div>
    <div ref="gantt-import-mapper-multiselect-wrapper" class="mt-1 d-inline-block w-80">
      <multiselect ref="gantt-import-mapper-multiselect" v-model="valueObj" :class="multiselectId" class="custom-dropdown-options gantt-import-mapper-cell-header enable-option-icon fit-label-fix fit-content-fix"
        :style="{ '--custom-multiselect-max-width': maxWidth }"
        :max-height="1000"
        :options="sanitizedOpts"
        track-by="value" 
        label="text" 
        :placeholder="''"
        :searchable="false" 
        :allow-empty="false"
        :showLabels="false"
        @input="onChange"
        @open="onSelectOpened">
        <template slot="option" slot-scope="props">
          <template v-if="isGroupHeader(props.option)">
            <span class="option__title option__group_header">{{ getOptionLabel(props.option) }}</span>
          </template>
          <template v-else>
            <font-awesome-icon class="selected-option-icon" v-if="valueObj.value == props.option.value" :icon="['far', 'check']" />
            <template v-if="isGroupOption(props.option)">
              <span class="option__title option__group_option">{{ getOptionLabel(props.option) }}</span>
            </template>
            <template v-else>
              <span class="option__title">{{ getOptionLabel(props.option) }}</span>
            </template>
          </template>
        </template>
      </multiselect>
    </div>
    <span>
        <b-popover
          :target="`HEADER_PARENT_${params.column.colId}`"
          v-if="canParent() || (direction() === 'v' && value !== null)"
          placement="top"
          triggers="hover"
          :content="$t('task.select_parent')">
        </b-popover>
      <button :id="`HEADER_PARENT_${params.column.colId}`" v-if="canParent() || (direction() === 'v' && value !== null)" class="btn-action" @click="selectParent"><font-awesome-icon :class="hasParent() ? 'active' : ''" :icon="['far','sitemap']"/></button> 
    </span>
    <span>
      <b-popover
        :target="`HEADER_OWNER_${params.column.colId}`"
        v-if="canOwn() && direction() === 'h'" 
        placement="top"
        triggers="hover"
        :content="owned() ? $t('task.manual_link') : $t('task.auto_link')">
      </b-popover>
      <button :id="`HEADER_OWNER_${params.column.colId}`" v-if="canOwn() && direction() === 'h'" class="btn-action" @click="selectOwner"><font-awesome-icon :class="owned() ? 'active' : ''" :icon="['far','link']"/></button> 
    </span>
  </div>
</template>

<script>
import Vue from 'vue';
import { cloneDeep } from 'lodash';
import { fieldOptions, initFieldOptions } from '@/selectOptions';
import { getCustomFieldInfo } from '@/helpers/custom-fields'
import Multiselect from 'vue-multiselect';
import { strRandom } from '@/helpers';

export default Vue.extend({
  name: 'SettingsHeaderComponent',
  components: {
    Multiselect
  },
  data() {
    return {
      value: null,
      valueObj: null, //the actual initial value will be assigned in beforeMount()
      options: fieldOptions,
      maxWidth: null
    }
  },
  created() {
    this.multiselectId = `multiselect-id-${strRandom(8)}`;
    // Create ResizeObserver
    this.observer = new ResizeObserver(entries => {
      if (entries.length > 0) {
        let width = this.params.eGridHeader.style.width;
        this.maxWidth = `${width.substring(0, width.length - 2) - 70}px`;
      }
    });
  },
  beforeMount() {
    if (this.params !== null) {
      
      const column = this.params.displayName;
      const mappedValue = this.params.context.componentParent.mappedValues[column];
      this.value = mappedValue ? mappedValue.value : null;
      
      // allow overriding the options presented
      if (this.params.options) {
        this.options = this.params.options;
      }
      else {
        this.options = cloneDeep(fieldOptions);
      }
    }

    initFieldOptions(this);
    
    if (this.params.context.componentParent.mode === 'TASKS') {
      getCustomFieldInfo(this, this.params.context.componentParent.mode === 'TASKS' ? 'TASK' : this.params.context.componentParent.mode).then(() => {
          // make sure the custom fields are selectable for import
        if (this.customFields) {
          for (const cfield of this.customFields) {
            const found = this.options.find(v => v.value === cfield.name);
            if (!found) {
              this.options.push({ value: cfield.name, text: cfield.displayName });
            }
          }
        }
      });
    }
    //initialize this.valueObj when sanitizedOpts is ready at this point.
    this.valueObj = this.sanitizedOpts.find(i => this.value == null? i.value == 'null' : i.value == this.value );
  },
  mounted() {
    this.setupGridHeaderWidthChangeObserver();
  },
  beforeDestroy() {
    this.cleanupGridHeaderWidthChangeObserver();
    this.multiselectId = null;
  },
  computed: {
    displayValue() {
      if (typeof this.value === 'undefined' ||
          this.value === null) {
        return this.value;
      }
      
      const mode = this.params.context.componentParent.mode;
      
      if (mode === "TASKS") {
        if (this.value.startsWith('summary')) {
          return this.$t('task_type.Project');
        }
        else if (this.value.startsWith('milestone')) {
          return this.$t('task_type.Milestone');
        }
        return this.$t('task_type.Task');
      }
      else if (mode === "COMPANY") {
        return this.$t('company.title');
      }
      else if (mode === "DEPARTMENT") {
        return this.$t('department.title');
      }
      else if (mode === "STAFF") {
        return this.$t('staff.title');
      }
      else if (mode === "CUSTOMER") {
        return this.$t('customer.title');
      }
      else if (mode === "PROJECT") {
        return this.$t('project.title');
      }
      else if (mode === "STAGE") {
        return this.$t('stage.title');
      }
      else if (mode === "TASK_TEMPLATE") {
        return this.$t('template.title');
      }
      else if (mode === "SKILL") {
        return this.$t('skill.title');
      }
      else if (mode === "RESOURCE") {
        return this.$t('resource.title');
      }
      else if (mode === "LOCATION") {
        return this.$t('location.title');
      }
      else if (mode === "REBATE") {
        return this.$t('rebate.title');
      }
      return '';
    },
    label() {
      return this.params.displayName;
    },
    sanitizedOpts() {
      const list = [];
      if (this.options == null) {
        return list;
      }
      if (this.options.length == 0) {
        return list;
      }
      let hasNullOption = false;
      let groupIndex = 0;
      //Vue-multiselect's option Group feature doesn't support the mix of simple option and group option
      //Custom logic is created to achieve the goal.
      for (let i = 0, len = this.options.length; i < len; i++) {
        if (Array.isArray(this.options[i].options)) {
          //// Process group list
          //1) generate a dummy (disabled) option with class/style as group header 
          list.push({
            text: this.options[i].label
            , value: `group_idx_${groupIndex++}`
            , groupHeader: true
            , $isDisabled: true //Make it disabled
          });
          //2) populate group options
          for (let j = 0, len = this.options[i].options.length; j < len; j++) {
            list.push({
              ...this.options[i].options[j], group: true
            })
          }
        } else if (this.options[i].value == null && !hasNullOption) {
          list.push({ ...this.options[i], value: 'null' });
          hasNullOption = true;
        } else {
          list.push(this.options[i]);
        }
      }
      return list;
    }
  },
  methods: {
    canParent() {
      return this.params.context.componentParent.canSelectParent(this.params.displayName) && (this.value === 'summary_name' || this.value === 'milestone_name' || this.value === 'name');
    },
    canOwn() {
      return this.params.options === null && this.params.context.componentParent.canSelectOwner(this.params.displayName, this.value) && (this.value && this.value !== 'summary_name' && this.value !== 'milestone_name' && this.value !== 'name');
    },
    owned() {
      return this.params.context.componentParent.getOwner(this.params.displayName);
    },
    direction() {
      return this.params.context.componentParent.getDirection(this.params.displayName);
    },
    canChangeDirection() {
      return this.params.options === null && this.value !== null && this.value !== 'summary_name' && this.value !== 'milestone_name' && this.value !== 'name';
    },
    hasParent() {
      return this.params.context.componentParent.getParent(this.params.displayName);
    },
    onChange() {
      this.value = this.valueObj.value == 'null'? null : this.valueObj.value;
      const column = this.params.displayName;
      const mappedValue = this.params.context.componentParent.mappedValues[column];
      const prevValue = mappedValue ? mappedValue.value : null;
      this.params.context.componentParent.setMappedValue(column, this.value);
      this.params.context.componentParent.updateSettings(this.params.displayName, this.value, prevValue, this.direction(), this.direction());
      this.params.context.componentParent.gridApi.redrawRows([]);
      
      if (this.direction() === 'v' &&
          !this.canChangeDirection()) {
        this.toggleDirection();
      }
    },
    selectParent() {
      this.params.context.componentParent.updateParent(this.params.displayName, this.value);
    },
    selectOwner() {
      this.params.context.componentParent.updateOwner(this.params.displayName, this.value);
    },
    toggleDirection() {
      const prevDirection = this.direction();
      this.params.context.componentParent.toggleDirection(this.params.displayName);
      this.params.context.componentParent.updateSettings(this.params.displayName, this.value, this.value, this.direction(), prevDirection);
    },
    getOptionLabel(value) { 
      return value?.text || '';
    },
    onSelectOpened() {
      const ms = this.$refs['gantt-import-mapper-multiselect'];
      ms.$refs.list.style.left = `${this.$refs['gantt-import-mapper-multiselect-wrapper'].getBoundingClientRect().left}px`;
      ms.$refs.list.style.position = 'fixed';
    },
    setupGridHeaderWidthChangeObserver() {
      this.$nextTick(() => {
        if (this.observer != null) {
          this.observer.observe(this.params.eGridHeader);
        }
      });
    },
    cleanupGridHeaderWidthChangeObserver() {
      if (this.observer != null) {
        this.observer.disconnect();
        this.observer = null;
      }
    },
    isGroupHeader(value) {
      return value?.groupHeader == true;
    },
    isGroupOption(value) {
      return value?.group == true;
    }
  }
})
</script>

<style lang="scss" scoped>
.w-80 {
  width: 80%;
}

.active {
  color: var(--projectal-orange);
}

.min-width-120 {
  min-width: 120px;
}

.min-height-17 {
  min-height: 17px;
}

.gantt-import-mapper-header-container {
  height: 49px;
}
</style>
